import React from "react";
import { TanstackSortingFn } from "../../../../utils/TableSorting";
import CustomBadge from "../../../ui/CustomBadge/CustomBadge";
import BaseTable from "../../../ui/CustomTanStackTable/CustomTanStackTable";
import ColumnVisibilitySelector from "../../../ui/ColumnVisibilitySelector/ColumnVisibilitySelector";
import useColumnVisibilityStore, {
  selectVisibleColumns,
} from "../../../../stores/columnVisibilityStore";
import { FiRefreshCcw } from "react-icons/fi";
import { TRANSACTION_TABLE_STATUS_MAP } from "../../constants/transactionConstants";
import {
  getMerchantName,
  getPaymentMethod,
} from "../../utils/transactionFormatters";
import {
  formatDateTimeToLocale,
  formatKES,
  getDisplayValue,
} from "../../../../utils/transactionutils";

// Always-visible columns (not controlled by the visibility selector)
const DEFAULT_COLUMN_KEYS = ["gatewayReference", "description", "status"];

const OPTIONAL_COLUMN_KEYS = [
  "amount",
  "transactionCharges",
  "transactionDate",
  "accountNumber",
  "paymentMethod",
];

const SUPER_ADMIN_OPTIONAL_KEYS = ["paymentAccountNumber", "merchant"];

const COLUMN_META = [
  { key: "gatewayReference", label: "Gateway Ref" },
  { key: "description", label: "Description" },
  { key: "status", label: "Status" },
  { key: "amount", label: "Amount" },
  { key: "transactionCharges", label: "Charges" },
  { key: "accountNumber", label: "Account Number" },
  { key: "paymentMethod", label: "Payment Type" },
  { key: "transactionDate", label: "Date" },
];

const SUPER_ADMIN_COLUMN_META = [
  { key: "gatewayReference", label: "Gateway Ref" },
  { key: "description", label: "Description" },
  { key: "status", label: "Status" },
  { key: "paymentAccountNumber", label: "Merchant Alias" },
  { key: "merchant", label: "Assigned Merchant" },
  { key: "amount", label: "Amount" },
  { key: "transactionCharges", label: "Charges" },
  { key: "accountNumber", label: "Account Number" },
  { key: "paymentMethod", label: "Payment Type" },
  { key: "transactionDate", label: "Date" },
];

function TransactionTableTanStack({
  data,
  totalElements = 0,
  pagination,
  onPaginationChange,
  totalPages = 0,
  isLoading = false,
  isFetching = false,
  onRowClick,
  onReassignClick,
  isSuperAdmin = false,
  merchantOptions = [],
  isMerchantOptionsError = false,
}) {
  const visibleOptionalKeys = useColumnVisibilityStore(
    selectVisibleColumns("transactions"),
  );
  const setVisibleOptionalKeys = useColumnVisibilityStore(
    (s) => s.setVisibleColumns,
  );

  const allOptionalKeys = React.useMemo(
    () =>
      isSuperAdmin
        ? [...SUPER_ADMIN_OPTIONAL_KEYS, ...OPTIONAL_COLUMN_KEYS]
        : OPTIONAL_COLUMN_KEYS,
    [isSuperAdmin],
  );

  const columnVisibility = React.useMemo(() => {
    const visibility = {};
    allOptionalKeys.forEach((key) => {
      visibility[key] = visibleOptionalKeys.includes(key);
    });
    return visibility;
  }, [visibleOptionalKeys, allOptionalKeys]);

  const columns = React.useMemo(
    () => [
      // Always-visible core columns
      {
        accessorKey: "gatewayReference",
        header: "Gateway Ref",
        cell: (info) => (
          <div
            className="max-w-[240px] truncate"
            title={getDisplayValue(info.getValue())}
          >
            {getDisplayValue(info.getValue())}
          </div>
        ),
        sortingFn: TanstackSortingFn,
      },
      {
        accessorKey: "description",
        header: "Description",
        cell: (info) => (
          <div className="max-w-[240px] whitespace-normal break-words leading-snug py-1">
            {getDisplayValue(info.getValue())}
          </div>
        ),
        sortingFn: TanstackSortingFn,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) => {
          const backendStatus = getDisplayValue(info.getValue());
          const mappedStatus = TRANSACTION_TABLE_STATUS_MAP[backendStatus];
          return (
            <CustomBadge color={mappedStatus} variant="light" size="sm">
              {backendStatus}
            </CustomBadge>
          );
        },
        size: 120,
        minSize: 100,
      },
      // Super-admin optional columns (first so they appear closest to the core columns)
      ...(isSuperAdmin
        ? [
            {
              accessorKey: "paymentAccountNumber",
              header: "Merchant Alias",
              cell: (info) => (
                <div
                  className="max-w-[120px] truncate"
                  title={getDisplayValue(info.getValue())}
                >
                  {getDisplayValue(info.getValue())}
                </div>
              ),
              sortingFn: TanstackSortingFn,
            },
            {
              accessorKey: "merchant",
              header: "Assigned Merchant",
              cell: (info) => (
                <div
                  className="max-w-[140px] truncate"
                  title={getMerchantName(info.getValue())}
                >
                  {getMerchantName(info.getValue())}
                </div>
              ),
              sortingFn: TanstackSortingFn,
            },
          ]
        : []),
      // Regular optional columns
      {
        accessorKey: "amount",
        header: "Amount",
        cell: (info) => (
          <div className="truncate" title={formatKES(info.getValue())}>
            {formatKES(info.getValue())}
          </div>
        ),
        sortingFn: TanstackSortingFn,
      },
      {
        accessorKey: "transactionCharges",
        header: "Charges",
        cell: (info) => (
          <div className="truncate" title={formatKES(info.getValue())}>
            {formatKES(info.getValue())}
          </div>
        ),
        sortingFn: TanstackSortingFn,
      },
      {
        accessorKey: "accountNumber",
        header: "Account Number",
        cell: (info) => (
          <div
            className="max-w-[120px] truncate"
            title={getDisplayValue(info.getValue())}
          >
            {getDisplayValue(info.getValue())}
          </div>
        ),
        sortingFn: TanstackSortingFn,
      },
      {
        id: "paymentMethod",
        header: "Payment Type",
        cell: (info) => {
          const { serviceCode, payoutAccountType, gatewayReference } =
            info.row.original;
          const value = getPaymentMethod(
            serviceCode,
            payoutAccountType,
            gatewayReference,
          );
          return (
            <div className="max-w-[150px] truncate" title={value}>
              {value}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: "transactionDate",
        header: "Date",
        cell: (info) => (
          <div
            className="max-w-[140px] truncate"
            title={formatDateTimeToLocale(info.getValue())}
          >
            {formatDateTimeToLocale(info.getValue())}
          </div>
        ),
        sortingFn: TanstackSortingFn,
      },
      // Actions — always last
      ...(isSuperAdmin
        ? [
            {
              id: "actions",
              header: "Actions",
              cell: (info) => {
                const _row = info.row.original;
                const merchantName =
                  _row.merchant?.merchantName || _row.merchant?.name || "";
                const isPaykit = merchantName.toLowerCase().includes("paykit");
                const merchantsUnavailable =
                  isMerchantOptionsError || merchantOptions.length === 0;
                const isDisabled = !isPaykit || merchantsUnavailable;
                const tooltipMessage = !isPaykit
                  ? "Cannot reassign currently: Assigned merchant is not Paykit"
                  : isMerchantOptionsError
                    ? "Cannot reassign: Failed to load merchants"
                    : merchantOptions.length === 0
                      ? "Cannot reassign: No merchants available"
                      : "Reassign Transaction";

                return (
                  <div className="flex gap-2">
                    <button
                      title={tooltipMessage}
                      className={
                        isDisabled
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-blue-500 hover:text-blue-700"
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isDisabled) {
                          onReassignClick?.(_row);
                        }
                      }}
                      disabled={isDisabled}
                    >
                      <FiRefreshCcw size={16} />
                    </button>
                  </div>
                );
              },
              enableSorting: false,
            },
          ]
        : []),
    ],
    [
      isSuperAdmin,
      onReassignClick,
      merchantOptions,
      isMerchantOptionsError,
    ],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <ColumnVisibilitySelector
          columns={isSuperAdmin ? SUPER_ADMIN_COLUMN_META : COLUMN_META}
          defaultColumnKeys={DEFAULT_COLUMN_KEYS}
          visibleColumnKeys={visibleOptionalKeys}
          onChange={(keys) => setVisibleOptionalKeys("transactions", keys)}
        />
      </div>
      <BaseTable
        data={data}
        columns={columns}
        columnVisibility={columnVisibility}
        pageSize={pagination.pageSize}
        totalPages={totalPages}
        totalElements={totalElements}
        pagination={pagination}
        isLoading={isLoading}
        isFetching={isFetching}
        onRowClick={onRowClick}
        onPaginationChange={onPaginationChange}
        paginationMode="server"
        emptyMessage="No Transactions Found"
        rowClassName={() =>
          "hover:bg-blue-50 cursor-pointer bg-white-50 min-h-[50px] "
        }
      />
    </div>
  );
}

export default TransactionTableTanStack;
